// Global Google Sign-In manager - ensures single initialization
let googleScriptLoaded = false
let googleInitialized = false
let currentCallback = null
let initializePromise = null

// Load Google Identity Services script once
export const loadGoogleScript = () => {
  if (googleScriptLoaded) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      googleScriptLoaded = true
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => {
      googleScriptLoaded = true
      resolve()
    }
    script.onerror = () => {
      console.error('Failed to load Google Identity Services script')
      reject(new Error('Failed to load Google script'))
    }
    document.body.appendChild(script)
  })
}

// Initialize Google Identity Services once globally
export const initializeGoogleSignIn = (clientId, callback) => {
  if (initializePromise) {
    // If initialization is in progress, update the callback
    if (callback) {
      currentCallback = callback
    }
    return initializePromise
  }

  if (googleInitialized && window.google?.accounts?.id) {
    // Already initialized, just update callback if provided
    if (callback) {
      currentCallback = callback
    }
    return Promise.resolve()
  }

  // Store the callback
  if (callback) {
    currentCallback = callback
  }

  initializePromise = loadGoogleScript().then(() => {
    if (!window.google?.accounts?.id) {
      throw new Error('Google Identity Services not available')
    }

    if (!googleInitialized) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          // Call the current callback
          if (currentCallback) {
            currentCallback(response)
          }
        },
        auto_select: false,
        cancel_on_tap_out: false,
      })
      googleInitialized = true
    }

    return Promise.resolve()
  })

  return initializePromise
}

// Render Google Sign-In button
export const renderGoogleButton = (element, options = {}) => {
  if (!window.google?.accounts?.id) {
    console.error('Google Identity Services not initialized')
    return
  }

  const defaultOptions = {
    theme: 'outline',
    size: 'large',
    width: 320,
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'left',
  }

  window.google.accounts.id.renderButton(element, { ...defaultOptions, ...options })
}

// Check if Google is ready
export const isGoogleReady = () => {
  return googleScriptLoaded && googleInitialized && window.google?.accounts?.id
}

// Update the callback dynamically
export const setGoogleCallback = (callback) => {
  currentCallback = callback
}
